import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
}

export function Select({
  value,
  defaultValue,
  onValueChange,
  disabled,
  placeholder,
  children,
  className,
  ...rest
}: SelectProps) {
  const rootProps: Partial<React.ComponentProps<typeof RadixSelect.Root>> = {};
  if (value !== undefined) rootProps.value = value;
  if (defaultValue !== undefined) rootProps.defaultValue = defaultValue;
  if (onValueChange) rootProps.onValueChange = onValueChange;
  if (disabled !== undefined) rootProps.disabled = disabled;
  return (
    <RadixSelect.Root {...rootProps}>
      <RadixSelect.Trigger
        className={cn(
          'inline-flex h-11 w-full items-center justify-between gap-2 rounded-md border border-parchment-border bg-parchment-light px-3 text-base sm:h-10 sm:text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          'disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
        aria-label={rest['aria-label']}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon className="text-parchment-muted">
          <ChevronDown className="h-4 w-4" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'z-50 max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)]',
            'overflow-hidden rounded-md border border-parchment-border bg-parchment-light shadow-parchment-card',
            'animate-fade-in',
          )}
        >
          <RadixSelect.Viewport className="p-1">{children}</RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

export const SelectItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixSelect.Item>
>(function SelectItem({ className, children, ...rest }, ref) {
  return (
    <RadixSelect.Item
      ref={ref}
      className={cn(
        'relative flex h-9 cursor-pointer select-none items-center rounded-sm py-1.5 pl-7 pr-2 text-sm',
        'hover:bg-parchment-dark/40 focus:bg-parchment-dark/40',
        'data-[state=checked]:bg-accent/10 data-[state=checked]:text-accent',
        'outline-none',
        className,
      )}
      {...rest}
    >
      <RadixSelect.ItemIndicator className="absolute left-2 inline-flex items-center">
        <Check className="h-3.5 w-3.5" />
      </RadixSelect.ItemIndicator>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  );
});
